import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import NectarDashboard from "@/pages/NectarDashboard";
import AuthPage from "@/pages/AuthPage";
import OnboardingPage from "@/pages/OnboardingPage";
import TrainingMode from "@/pages/TrainingMode";
import SettingsPage from "@/pages/SettingsPage";
import CalendarConnectionsPage from "@/pages/CalendarConnectionsPage";  // calendar-multiconta-1.3
import AdminAgentPage from "@/pages/AdminAgentPage";
import AdminPluggyPage from "@/pages/AdminPluggyPage";
import PlansPage from "@/pages/PlansPage";  // BILLING-S1B — pricing pública
import LegalPage from "@/pages/LegalPage";  // R4 — privacidade + termos
import AdminUsersPage from "@/pages/AdminUsersPage";  // D1 — visibilidade do beta
import InstallPrompt from "@/components/pwa/InstallPrompt";  // C1 — PWA install
import UpdateAvailable from "@/components/pwa/UpdateAvailable";  // C1 — nova versão
import { migrateLegacyStorage } from "@/utils/migrateLegacyStorage";

// Must run at module load, before AuthProvider reads localStorage in its initializer.
migrateLegacyStorage();

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#0B0F1A" }}>
      <div className="text-[#94A3B8] text-sm">Carregando...</div>
    </div>
  );
}

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.onboarding_completed) return <Navigate to="/onboarding" replace />;
  return children;
}

function OnboardingRoute() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.onboarding_completed) return <Navigate to="/" replace />;
  return <OnboardingPage />;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user && user.onboarding_completed) return <Navigate to="/" replace />;
  if (user && !user.onboarding_completed) return <Navigate to="/onboarding" replace />;
  return children;
}

// Sessão 5A: gate adicional pra rotas /admin/*. Defesa visual; segurança
// real é o 403 do backend (require_admin). Não-admin não vê a tela vazia
// nem mensagem de "acesso negado" — só redirect pra raiz, esconde a rota.
function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user || !user.is_admin) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><AuthPage /></PublicRoute>} />
      <Route path="/onboarding" element={<OnboardingRoute />} />
      {/* BILLING-S1B — /planos é público (sem PublicRoute wrapper que
          redireciona authenticated; user logado também pode ver). */}
      <Route path="/planos" element={<PlansPage />} />
      {/* R4 — Páginas legais públicas. Sem auth, indexáveis. */}
      <Route path="/privacidade" element={<LegalPage slug="privacidade" />} />
      <Route path="/termos" element={<LegalPage slug="termos" />} />
      <Route path="/" element={<PrivateRoute><NectarDashboard /></PrivateRoute>} />
      <Route path="/training" element={<PrivateRoute><TrainingMode /></PrivateRoute>} />
      <Route path="/settings" element={<PrivateRoute><SettingsPage /></PrivateRoute>} />
      <Route path="/configuracoes/conexoes" element={<PrivateRoute><CalendarConnectionsPage /></PrivateRoute>} />
      <Route path="/admin/agent" element={<PrivateRoute><AdminRoute><AdminAgentPage /></AdminRoute></PrivateRoute>} />
      <Route path="/admin/pluggy" element={<PrivateRoute><AdminRoute><AdminPluggyPage /></AdminRoute></PrivateRoute>} />
      <Route path="/admin/usuarios" element={<PrivateRoute><AdminRoute><AdminUsersPage /></AdminRoute></PrivateRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: '#121826',
            border: '1px solid #1F2A44',
            color: '#F1F5F9',
          },
        }}
      />
      {/* C1 — PWA: banner de instalação + banner de nova versão. Ambos
          são puramente aditivos e não-bloqueantes; ficam fora do
          AuthProvider/Router pra serem visíveis em /login, /onboarding,
          em qualquer rota. */}
      <InstallPrompt />
      <UpdateAvailable />
    </div>
  );
}

export default App;
